import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Link,
    Text,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import FormBase from '@/components/forms/new/FormBase';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import { breadCrumbStyling } from 'components/forms/new/formStyles';
import { useQuery } from 'hooks/useCustomQuery';
import { validationSchema } from './consts';
import walkthroughIds from './walkthroughIds';
import { createCatalogItem, getCatalogItems } from './api';
import NameField from './formFields/NameField';
import ParentField from './formFields/ParentField';
import DescriptionField from './formFields/DescriptionField';
import SkuField from './formFields/SkuField';
import OfferingIdField from './formFields/OfferingIdField';
import VisibilityField from './formFields/VisibilityField';
import RuleField from './formFields/RuleField';

const initialValues = {
    name: '',
    parentId: '',
    description: '',
    sku: '',
    offeringId: '',
    visibility: 'visible',
    rule: '',
};

const breadCrumbTrail = [
    { label: '8×8 Catalog', path: '/globaladmin/8x8-catalog' },
    { label: 'Add Offering', path: '' },
];

export default function EightByEightCatalogCreate({
    setCatalogAction,
    setSuccessAction,
    platformId,
    requiredScope,
}) {
    const [error, setError] = useState(null);
    const { darkMode } = useSelector((state) => state.settings);
    const { createMode } = walkthroughIds;

    const { data: catalogItems = [], isLoading: isLoadingItems } = useQuery({
        queryKey: ['8x8CatalogItems', platformId],
        queryFn: () =>
            getCatalogItems(platformId, requiredScope).then((r) => r.data),
        staleTime: 0,
        cacheTime: 0,
        enabled: !!platformId,
        refetchOnWindowFocus: false,
    });

    if (isLoadingItems) return <LoadingIndicator />;

    const finalFormSubmit = async (fields, actions) => {
        setError(null);
        setSuccessAction(null);

        createCatalogItem(
            {
                ...fields,
                parentId: fields.parentId || null,
                rule: fields.rule || null,
            },
            platformId,
            requiredScope,
        )
            .then((response) => {
                if (response.status === 200) {
                    setSuccessAction('Create');
                    setCatalogAction('Get');
                    setTimeout(() => setSuccessAction(null), 6000);
                } else {
                    setError(
                        response.response?.data?.message ||
                            'Failed to create catalog item.',
                    );
                }
            })
            .catch((err) => {
                setError(
                    err?.response?.data?.message ||
                        err?.data?.message ||
                        'Something went wrong when creating a catalog item.',
                );
            })
            .finally(() => {
                actions.setSubmitting(false);
            });
    };

    return (
        <>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        <BreadcrumbLink
                            as={Link}
                            to={crumb.path}
                            onClick={
                                index === 0
                                    ? () => setCatalogAction('Get')
                                    : undefined
                            }>
                            {crumb.label}
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>
            <Text sx={{ fontSize: '20', fontWeight: '500' }}>Add Offering</Text>
            <FormBase
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                validationSchema={validationSchema}
                ufBackBtn={createMode.ufBackBtn}
                ufSaveBtn={createMode.ufCreateBtn}
                customClick={true}
                onBack={() => setCatalogAction('Get')}>
                {({ errors = {}, touched = {} }) =>
                    [
                        <NameField
                            key="name"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <ParentField
                            key="parentId"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                            catalogItems={catalogItems}
                        />,
                        <DescriptionField
                            key="description"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <SkuField
                            key="sku"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <OfferingIdField
                            key="offeringId"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <VisibilityField
                            key="visibility"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <RuleField key="rule" darkMode={darkMode} />,
                        error && (
                            <Alert
                                key="error"
                                status="error"
                                sx={{ margin: '25px 0' }}>
                                <AlertIcon />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        ),
                    ].filter(Boolean)
                }
            </FormBase>
        </>
    );
}
